import { useEffect, useRef, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { useAppDispatch } from '../../../app/store/store';
import {
  addItemToCart,
  deleteItemFromCart,
  deleteVerificationFromCart,
  deletePassportFromCart,
} from '../../../entities/cart/model';
import { Item } from '../../../types/interfaces';
import {
  adjustOnQuantityChange,
  adjustOnVerificationChange,
  adjustOnPassportChange,
} from '../lib/quantityConstraints';

function parseInput(value: number): number {
  return parseInt(value ? value.toString() : '1', 10);
}

/**
 * Доступный потолок количества для KZ-товара (остаток − резерв). Для
 * RU и товаров без склада ограничения нет (Infinity). Клампим только
 * при положительном available, чтобы не залочить строку в 0.
 */
function getMaxQuantity(item: Item): number {
  const available = item?.available;
  if (item?.country === 'kz' && available != null && Number(available) >= 1) {
    return Number(available);
  }
  return Infinity;
}

export function useCartItemQuantity(item: Item) {
  const [quantity, setQuantity] = useState(item.quantity);
  const [verificationQuantity, setVerificationQuantity] = useState(item.verification_quantity);
  const [passportQuantity, setPassportQuantity] = useState(item.passport_quantity || 0);
  const dispatch = useAppDispatch();

  // Целевая тройка (q/vq/pq) нашего последнего локального ввода, по которой
  // мы ждём подтверждения от сервера. null = несведённой локальной правки
  // нет, серверное значение принимаем как есть.
  const pendingRef = useRef<{ q: number; vq: number; pq: number } | null>(null);

  // Синхронизация локального state из серверного: по примитивам, а НЕ по
  // объекту `item` целиком. `selectCartWithCalculatedPrices` через
  // calculateCartPrices пересоздаёт объекты массива на каждое обновление
  // cart-слайса (валюта, amount_previous_order и т.п.) — зависимость [item]
  // дёргала бы эффект вхолостую.
  //
  // Главное: при правке количества/паспорта/поверки запрос уходит с
  // debounce'ом, и при кликах с частотой около окна debounce успевает
  // прилететь серверное эхо ПРЕДЫДУЩЕГО диспатча, пока пользователь уже
  // кликнул дальше. Раньше оно перетирало локальный ввод, и цифры
  // перескакивали (2→1→2). Поэтому принимаем серверное значение только
  // если оно совпало с нашим последним вводом (pendingRef) — тогда правка
  // сведена; не совпало — это устаревшее эхо, игнорируем. Когда правок нет
  // (pendingRef === null), серверное значение — источник истины.
  useEffect(() => {
    const sq = item.quantity;
    const svq = item.verification_quantity;
    const spq = item.passport_quantity || 0;
    const pending = pendingRef.current;
    if (pending) {
      if (pending.q === sq && pending.vq === svq && pending.pq === spq) {
        pendingRef.current = null;
      } else {
        return;
      }
    }
    setQuantity(sq);
    setVerificationQuantity(svq);
    setPassportQuantity(spq);
  }, [item.quantity, item.verification_quantity, item.passport_quantity]);

  const dispatchUpdate = useCallback(
    (q: number, vq: number, pq: number) => {
      pendingRef.current = { q, vq, pq };
      dispatch(
        addItemToCart({
          id: item.id,
          quantity: q,
          verification_item: item.verification_item ? item.verification_item.id.toString() : null,
          verification_quantity: item.verification_item ? vq : 0,
          passport_item: item.passport_item ? item.passport_item.id.toString() : null,
          passport_quantity: item.passport_item ? pq : 0,
          inCart: item.inCart,
        })
      );
    },
    [dispatch, item.id, item.verification_item, item.passport_item, item.inCart]
  );

  const debouncedUpdate = useRef(
    debounce((q: number, vq: number, pq: number) => {
      if (item.id != null) {
        dispatchUpdate(q, vq, pq);
      }
    }, 500)
  ).current;

  const handleQuantityChange = (value: number) => {
    // Жёсткий потолок по доступному остатку (только KZ). Не даём
    // увеличить количество в корзине выше available — ни кнопкой «+»,
    // ни вводом руками.
    const parsed = Math.min(parseInput(value), getMaxQuantity(item));
    const result = adjustOnQuantityChange(parsed, verificationQuantity, passportQuantity);
    setQuantity(result.quantity);
    setVerificationQuantity(result.verificationQuantity);
    setPassportQuantity(result.passportQuantity);
    // Фиксируем цель сразу при вводе, а не в момент диспатча: эхо запросов,
    // прилетевших за время debounce-окна, не должно перетереть свежий ввод.
    pendingRef.current = { q: result.quantity, vq: result.verificationQuantity, pq: result.passportQuantity };
    debouncedUpdate(result.quantity, result.verificationQuantity, result.passportQuantity);
  };

  const handleVerificationChange = (value: number) => {
    const parsed = parseInput(value);
    const result = adjustOnVerificationChange(quantity, parsed, passportQuantity);
    setQuantity(result.quantity);
    setVerificationQuantity(result.verificationQuantity);
    setPassportQuantity(result.passportQuantity);
    pendingRef.current = { q: result.quantity, vq: result.verificationQuantity, pq: result.passportQuantity };
    debouncedUpdate(result.quantity, result.verificationQuantity, result.passportQuantity);
  };

  const handlePassportChange = (value: number) => {
    const parsed = parseInput(value);
    const result = adjustOnPassportChange(quantity, verificationQuantity, parsed);
    setQuantity(result.quantity);
    setVerificationQuantity(result.verificationQuantity);
    setPassportQuantity(result.passportQuantity);
    pendingRef.current = { q: result.quantity, vq: result.verificationQuantity, pq: result.passportQuantity };
    debouncedUpdate(result.quantity, result.verificationQuantity, result.passportQuantity);
  };

  // Удаление меняет значения на сервере (обнуляет поверку/паспорт), поэтому
  // снимаем pendingRef — иначе sync-эффект счёл бы серверное эхо «чужим» и
  // не применил бы обнуление.
  const removeFromCart = () => {
    pendingRef.current = null;
    dispatch(deleteItemFromCart(item.id));
  };

  const removeVerification = (id: number) => {
    pendingRef.current = null;
    dispatch(
      deleteVerificationFromCart({
        id,
        quantity,
        verification_item: item.verification_item?.id.toString() || null,
        passport_item: item.passport_item?.id.toString() || null,
        passport_quantity: passportQuantity,
      })
    );
  };

  const removePassport = (id: number) => {
    pendingRef.current = null;
    dispatch(
      deletePassportFromCart({
        id,
        quantity,
        verification_item: item.verification_item?.id.toString() || null,
        passport_item: item.passport_item?.id.toString() || null,
      })
    );
  };

  return {
    quantity,
    verificationQuantity,
    passportQuantity,
    handleQuantityChange,
    handleVerificationChange,
    handlePassportChange,
    removeFromCart,
    removeVerification,
    removePassport,
    dispatchUpdate,
  };
}
